import React, { useEffect, useState, useRef } from 'react';
import { ShieldCheckIcon, PhoneOffIcon, FileTextIcon, ZapIcon, CheckCircleIcon, XCircleIcon, LockIcon, AlertTriangleIcon, StarIcon, ArrowDownIcon, ClockIcon, TrendingUpIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, {
      threshold: 0.1
    });
    const elements = document.querySelectorAll('.fade-in');
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}
function StickyTopBar() {
  return <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 py-2 px-4 overflow-hidden" data-id="element-1">
      <div className="flex items-center justify-center gap-6 text-white text-sm font-semibold font-body" data-id="element-2">
        <span className="flex items-center gap-2" data-id="element-3">
          <ZapIcon className="w-4 h-4 text-yellow-300" data-id="element-4" />
          ACCESO INSTANTÁNEO
        </span>
        <span className="text-yellow-300" data-id="element-5">·</span>
        <span data-id="element-6">
          Solo <strong className="text-yellow-300" data-id="element-7">$27</strong>
        </span>
        <span className="text-yellow-300" data-id="element-8">·</span>
        <span className="flex items-center gap-1" data-id="element-9">
          <ShieldCheckIcon className="w-4 h-4 text-yellow-300" data-id="element-10" />
          Garantía de Satisfacción
        </span>
      </div>
    </div>;
}
function HeroSection() {
  return <section className="relative min-h-screen flex items-center justify-center circuit-pattern circuit-lines pt-10" style={{
    background: 'linear-gradient(135deg, #0a0e1a 0%, #0d1117 50%, #0a0f1e 100%)'
  }} data-id="element-11">
      {/* Red glow top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{
      background: 'radial-gradient(circle, #dc2626, transparent 70%)'
    }} data-id="element-12" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 text-center" data-id="element-13">
        {/* URGENTE badge */}
        <div className="inline-flex items-center gap-2 bg-red-600 text-white text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full mb-6 animate-pulse" data-id="element-14">
          <AlertTriangleIcon className="w-3 h-3" data-id="element-15" />
          URGENTE — LEE ESTO AHORA
        </div>

        {/* H1 */}
        <h1 className="font-heading font-black text-white leading-none mb-6" style={{
        fontSize: 'clamp(2.2rem, 6vw, 4.5rem)',
        letterSpacing: '-0.02em'
      }} data-id="element-16">
          Cómo Volverte{' '}
          <span className="text-red-500 red-glow" data-id="element-17">"Invisible"</span>
          <br data-id="element-18" />
          para los Cobradores de Deudas
          <br data-id="element-19" />
          <span className="text-yellow-400 price-glow" data-id="element-20">en 72 Horas</span>{' '}
          <span className="text-slate-300" data-id="element-21">(Legalmente)</span>
        </h1>

        {/* Subhead */}
        <p className="font-body text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed" style={{
        fontSize: 'clamp(1rem, 2.5vw, 1.3rem)'
      }} data-id="element-22">
          El Protocolo Exacto para{' '}
          <strong className="text-white" data-id="element-23">Detener el Acoso Telefónico</strong>,{' '}
          <strong className="text-white" data-id="element-24">Silenciar las Amenazas</strong> y{' '}
          <strong className="text-white" data-id="element-25">Borrar Registros Negativos</strong> de
          tu Crédito
          <br data-id="element-26" />
          <span className="text-red-400" data-id="element-27">
            sin pagarle un centavo a un abogado.
          </span>
        </p>

        {/* Product image */}
        <div className="flex justify-center mb-10" data-id="element-28">
          <div className="relative" style={{
          filter: 'drop-shadow(0 20px 60px rgba(220, 38, 38, 0.3))'
        }} data-id="element-29">
            <img src="https://cdn.magicpatterns.com/uploads/8Rmf5AWfXXSWWPS6Ab3Eya/heylloyd-image-1772487533727.png" alt="El Protocolo Fantasma Sin Deudas — Guía Digital" className="w-64 md:w-80 mx-auto" data-id="element-30" />
          </div>
        </div>

        {/* CTA */}
        <a href="#oferta" className="btn-cta inline-block bg-red-600 hover:bg-red-500 text-white font-heading font-bold text-xl md:text-2xl uppercase tracking-wide px-10 py-5 rounded-lg transition-colors duration-200 mb-4" data-id="element-31">
          SÍ, QUIERO VOLVERME INVISIBLE AHORA →
        </a>

        <p className="font-body text-slate-400 text-sm" data-id="element-32">
          <LockIcon className="inline w-3 h-3 mr-1" data-id="element-33" />
          Pago 100% seguro · Acceso instantáneo · Solo $27
        </p>

        {/* Scroll indicator */}
        <div className="mt-12 flex justify-center" data-id="element-34">
          <ArrowDownIcon className="w-6 h-6 text-slate-500 animate-bounce" data-id="element-35" />
        </div>
      </div>
    </section>;
}
function OpeningHookSection() {
  return <section className="py-16 px-4" style={{
    background: '#0d1117'
  }} data-id="element-36">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-37">
        <div className="highlight-box p-6 md:p-8 rounded-r-lg mb-8" data-id="element-38">
          <p className="font-body text-slate-200 text-lg md:text-xl leading-relaxed italic" data-id="element-39">
            "Detente por un segundo. Siente ese{' '}
            <strong className="text-white" data-id="element-40">nudo en el estómago</strong> que
            aparece cada vez que tu teléfono vibra con un{' '}
            <span className="text-red-400 font-semibold" data-id="element-41">
              'Número Desconocido'
            </span>{' '}
            o{' '}
            <span className="text-red-400 font-semibold" data-id="element-42">
              'Fraude Potencial'
            </span>
            ..."
          </p>
        </div>

        <div className="space-y-5 font-body text-slate-300 text-base md:text-lg leading-relaxed" data-id="element-43">
          <p data-id="element-44">
            Esa sensación de que tu propia casa ya no es un refugio, sino una{' '}
            <strong className="text-white" data-id="element-45">zona de guerra financiera</strong>.
          </p>
          <p data-id="element-46">
            Sé lo que es cenar con tu familia y tener miedo de que el teléfono
            suene. Sé lo que es abrir el buzón y sentir un escalofrío al ver un
            sobre con <strong className="text-red-400" data-id="element-47">letras rojas</strong>.
          </p>
          <p className="font-heading font-bold text-2xl md:text-3xl text-white text-center py-4" style={{
          letterSpacing: '0.02em'
        }} data-id="element-48">
            Pero hoy,{' '}
            <span className="text-red-500" data-id="element-49">ese miedo se termina.</span>
          </p>
        </div>
      </div>
    </section>;
}
function ProblemSection() {
  return <section className="py-16 px-4" style={{
    background: '#0a0e1a'
  }} data-id="element-50">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-51">
        <div className="text-center mb-10" data-id="element-52">
          <span className="inline-block bg-red-900 bg-opacity-50 text-red-400 text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full border border-red-800 mb-4" data-id="element-53">
            LA VERDAD QUE NADIE TE DICE
          </span>
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase" data-id="element-54">
            Las Agencias de Cobranza Son{' '}
            <span className="text-red-500" data-id="element-55">Máquinas de Miedo</span>
          </h2>
        </div>

        <p className="font-body text-slate-300 text-base md:text-lg leading-relaxed mb-8 text-center" data-id="element-56">
          No son departamentos de justicia. Compran deudas por{' '}
          <strong className="text-yellow-400" data-id="element-57">centavos</strong>. Su único poder
          es tu{' '}
          <strong className="text-white" data-id="element-58">
            ignorancia de las leyes federales
          </strong>{' '}
          (FDCPA y FCRA).
        </p>

        <div className="space-y-4" data-id="element-59">
          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-60">
            <div className="flex-shrink-0 w-10 h-10 bg-red-900 bg-opacity-50 rounded-full flex items-center justify-center" data-id="element-61">
              <AlertTriangleIcon className="w-5 h-5 text-red-400" data-id="element-62" />
            </div>
            <div data-id="element-63">
              <p className="font-body font-semibold text-white mb-1" data-id="element-64">
                Te mienten diciendo que pueden arrestarte.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-65">
                Esto es ILEGAL bajo la FDCPA. Pero lo hacen porque funciona.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-66">
            <div className="flex-shrink-0 w-10 h-10 bg-red-900 bg-opacity-50 rounded-full flex items-center justify-center" data-id="element-67">
              <AlertTriangleIcon className="w-5 h-5 text-red-400" data-id="element-68" />
            </div>
            <div data-id="element-69">
              <p className="font-body font-semibold text-white mb-1" data-id="element-70">
                Te acosan en tu trabajo para avergonzarte.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-71">
                Calculan que el miedo social te hará pagar antes de saber tus
                derechos.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-72">
            <div className="flex-shrink-0 w-10 h-10 bg-red-900 bg-opacity-50 rounded-full flex items-center justify-center" data-id="element-73">
              <AlertTriangleIcon className="w-5 h-5 text-red-400" data-id="element-74" />
            </div>
            <div data-id="element-75">
              <p className="font-body font-semibold text-white mb-1" data-id="element-76">
                Amenazan con destruir tu crédito para siempre.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-77">
                Se aprovechan de tu decencia para extorsionarte emocionalmente.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-yellow-400 bg-opacity-10 border border-yellow-500 border-opacity-30 rounded-lg p-5 text-center" data-id="element-78">
          <p className="font-body text-yellow-300 font-semibold text-base md:text-lg" data-id="element-79">
            Lo hacen porque saben que el{' '}
            <strong className="text-yellow-400 text-xl" data-id="element-80">99%</strong> de las
            personas simplemente se rinden y pagan, o viven escondidas.
          </p>
        </div>
      </div>
    </section>;
}
function WhyFailedSection() {
  return <section className="py-16 px-4" style={{
    background: '#0d1117'
  }} data-id="element-81">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-82">
        <div className="text-center mb-10" data-id="element-83">
          <span className="inline-block bg-slate-800 text-slate-400 text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full border border-slate-700 mb-4" data-id="element-84">
            POR QUÉ LO QUE INTENTASTE NO FUNCIONÓ
          </span>
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase" data-id="element-85">
            Estas "Soluciones" Solo{' '}
            <span className="text-red-500" data-id="element-86">Empeoran las Cosas</span>
          </h2>
        </div>

        <div className="space-y-4" data-id="element-87">
          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-88">
            <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-89" />
            <div data-id="element-90">
              <p className="font-body font-bold text-white mb-1" data-id="element-91">
                Bloquear números
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-92">
                Es como intentar tapar el sol con un dedo. Ellos tienen{' '}
                <strong className="text-slate-300" data-id="element-93">
                  miles de líneas rotativas
                </strong>
                .
              </p>
            </div>
          </div>

          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-94">
            <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-95" />
            <div data-id="element-96">
              <p className="font-body font-bold text-white mb-1" data-id="element-97">
                Hacer "pagos pequeños"
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-98">
                <strong className="text-red-400" data-id="element-99">
                  Esta es la peor trampa.
                </strong>{' '}
                Al pagar $5, "revives" legalmente una deuda vieja y les das{' '}
                <strong className="text-slate-300" data-id="element-100">7 años más</strong> de poder
                para demandarte.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-101">
            <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-102" />
            <div data-id="element-103">
              <p className="font-body font-bold text-white mb-1" data-id="element-104">
                Rogar por piedad
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-105">
                Los cobradores están entrenados para ignorar tus lágrimas.{' '}
                <strong className="text-slate-300" data-id="element-106">
                  Solo responden ante la ley escrita.
                </strong>
              </p>
            </div>
          </div>

          <div className="card-dark rounded-lg p-5 flex items-start gap-4" data-id="element-107">
            <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-108" />
            <div data-id="element-109">
              <p className="font-body font-bold text-white mb-1" data-id="element-110">
                Ignorar el problema
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-111">
                Tu crédito se desangra mes a mes mientras esperas que "se
                olviden de ti".{' '}
                <strong className="text-red-400" data-id="element-112">No lo harán.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function MechanismSection() {
  return <section className="py-16 px-4 circuit-pattern" style={{
    background: 'linear-gradient(135deg, #0a0e1a, #0f1520)'
  }} data-id="element-113">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-114">
        <div className="text-center mb-10" data-id="element-115">
          <span className="inline-block bg-red-600 text-white text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full mb-4" data-id="element-116">
            EL ARMA SECRETA
          </span>
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase mb-4" data-id="element-117">
            El Mecanismo de{' '}
            <span className="text-red-500" data-id="element-118">"Invisibilidad Táctica"</span>
          </h2>
          <p className="font-body text-slate-300 text-base md:text-lg leading-relaxed" data-id="element-119">
            No vamos a "huir". Vamos a atacar su punto más débil:{' '}
            <strong className="text-yellow-400" data-id="element-120">
              Su falta de documentación legal.
            </strong>
          </p>
        </div>

        <div className="bg-yellow-400 bg-opacity-10 border border-yellow-500 border-opacity-40 rounded-xl p-6 md:p-8 mb-8" data-id="element-121">
          <p className="font-body text-white text-base md:text-lg leading-relaxed mb-4" data-id="element-122">
            Cuando una deuda se vende, el{' '}
            <strong className="text-yellow-400 text-2xl" data-id="element-123">60%</strong> de las
            veces pierden el contrato original con tu firma.
          </p>
          <p className="font-body text-yellow-300 font-bold text-lg" data-id="element-124">
            Sin ese papel, la deuda NO EXISTE legalmente.
          </p>
        </div>

        <p className="font-body text-slate-300 text-base md:text-lg leading-relaxed mb-6 text-center" data-id="element-125">
          El <strong className="text-white" data-id="element-126">Protocolo Fantasma</strong> te
          enseña a enviar las{' '}
          <strong className="text-yellow-400" data-id="element-127">3 cartas exactas</strong> que
          obligan a la agencia a demostrar que tienen el derecho legal de
          cobrarte.
        </p>

        <p className="font-body text-slate-300 text-center mb-6" data-id="element-128">
          Si no pueden (y usualmente{' '}
          <strong className="text-red-400" data-id="element-129">no pueden</strong>), la ley federal
          los obliga a:
        </p>

        <div className="space-y-3" data-id="element-130">
          <div className="flex items-center gap-4 bg-green-900 bg-opacity-20 border border-green-700 border-opacity-30 rounded-lg p-4" data-id="element-131">
            <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" data-id="element-132" />
            <p className="font-body text-white font-semibold" data-id="element-133">
              1. Dejar de llamarte{' '}
              <strong className="text-green-400" data-id="element-134">para siempre.</strong>
            </p>
          </div>
          <div className="flex items-center gap-4 bg-green-900 bg-opacity-20 border border-green-700 border-opacity-30 rounded-lg p-4" data-id="element-135">
            <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" data-id="element-136" />
            <p className="font-body text-white font-semibold" data-id="element-137">
              2. Borrar el registro negativo de Experian, Equifax y TransUnion{' '}
              <strong className="text-green-400" data-id="element-138">de inmediato.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function WhatYouGetSection() {
  return <section className="py-16 px-4" style={{
    background: '#0d1117'
  }} data-id="element-139">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-140">
        <div className="text-center mb-10" data-id="element-141">
          <span className="inline-block bg-slate-800 text-yellow-400 text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full border border-yellow-700 border-opacity-40 mb-4" data-id="element-142">
            TODO LO QUE RECIBES
          </span>
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase" data-id="element-143">
            Tu Arsenal Completo de{' '}
            <span className="text-yellow-400" data-id="element-144">Defensa Legal</span>
          </h2>
        </div>

        <div className="space-y-4" data-id="element-145">
          <div className="card-dark rounded-xl p-5 flex items-start gap-4 border-l-4 border-yellow-400" data-id="element-146">
            <FileTextIcon className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-0.5" data-id="element-147" />
            <div data-id="element-148">
              <p className="font-body font-bold text-white text-base mb-1" data-id="element-149">
                📘 La Guía Maestra "Fantasma Sin Deudas" (PDF)
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-150">
                El mapa paso a paso para ejecutar el protocolo en menos de una
                hora.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-xl p-5 flex items-start gap-4 border-l-4 border-red-500" data-id="element-151">
            <PhoneOffIcon className="w-8 h-8 text-red-400 flex-shrink-0 mt-0.5" data-id="element-152" />
            <div data-id="element-153">
              <p className="font-body font-bold text-white text-base mb-1" data-id="element-154">
                🔇 Plantilla #1: El Disparador de Silencio
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-155">
                La carta legal que detiene las llamadas a tu casa y trabajo en{' '}
                <strong className="text-red-400" data-id="element-156">72 horas</strong> bajo pena de
                multa de{' '}
                <strong className="text-yellow-400" data-id="element-157">$1,000 por llamada</strong>.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-xl p-5 flex items-start gap-4 border-l-4 border-blue-500" data-id="element-158">
            <ShieldCheckIcon className="w-8 h-8 text-blue-400 flex-shrink-0 mt-0.5" data-id="element-159" />
            <div data-id="element-160">
              <p className="font-body font-bold text-white text-base mb-1" data-id="element-161">
                🛡️ Plantilla #2: El Escudo de Validación
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-162">
                El documento que exige las pruebas físicas que ellos{' '}
                <strong className="text-white" data-id="element-163">casi nunca tienen</strong>.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-xl p-5 flex items-start gap-4 border-l-4 border-green-500" data-id="element-164">
            <TrendingUpIcon className="w-8 h-8 text-green-400 flex-shrink-0 mt-0.5" data-id="element-165" />
            <div data-id="element-166">
              <p className="font-body font-bold text-white text-base mb-1" data-id="element-167">
                📈 Plantilla #3: El Borrador del Buró
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-168">
                Cómo forzar a las agencias de crédito a eliminar registros
                inexactos o no verificables de Experian, Equifax y TransUnion.
              </p>
            </div>
          </div>

          <div className="card-dark rounded-xl p-5 flex items-start gap-4 border-l-4 border-purple-500" data-id="element-169">
            <ZapIcon className="w-8 h-8 text-purple-400 flex-shrink-0 mt-0.5" data-id="element-170" />
            <div data-id="element-171">
              <p className="font-body font-bold text-white text-base mb-1" data-id="element-172">
                ⚡ El Guión de Poder de 10 Segundos
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-173">
                Qué decir exactamente si llegas a contestar para que el cobrador{' '}
                <strong className="text-white" data-id="element-174">
                  cuelgue por miedo a ser demandado por TI
                </strong>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ForWhoSection() {
  return <section className="py-16 px-4" style={{
    background: '#0a0e1a'
  }} data-id="element-175">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-176">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-177">
          {/* For */}
          <div data-id="element-178">
            <h3 className="font-heading font-bold text-green-400 text-2xl uppercase mb-6 flex items-center gap-2" data-id="element-179">
              <CheckCircleIcon className="w-6 h-6" data-id="element-180" />
              Esto ES para Ti Si...
            </h3>
            <div className="space-y-3" data-id="element-181">
              <div className="flex items-start gap-3" data-id="element-182">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" data-id="element-183" />
                <p className="font-body text-slate-300 text-sm" data-id="element-184">
                  Tienes deudas en USA de tarjetas, facturas médicas o préstamos
                  en "collections".
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-185">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" data-id="element-186" />
                <p className="font-body text-slate-300 text-sm" data-id="element-187">
                  Eres padre o madre que quiere recuperar la paz en su hogar y
                  dejar de vivir con miedo al teléfono.
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-188">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" data-id="element-189" />
                <p className="font-body text-slate-300 text-sm" data-id="element-190">
                  Quieres limpiar tu crédito para comprar una casa o un auto,
                  pero tienes manchas del pasado que no te dejan avanzar.
                </p>
              </div>
            </div>
          </div>

          {/* Not for */}
          <div data-id="element-191">
            <h3 className="font-heading font-bold text-red-400 text-2xl uppercase mb-6 flex items-center gap-2" data-id="element-192">
              <XCircleIcon className="w-6 h-6" data-id="element-193" />
              Esto NO es para Ti Si...
            </h3>
            <div className="space-y-3" data-id="element-194">
              <div className="flex items-start gap-3" data-id="element-195">
                <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-196" />
                <p className="font-body text-slate-300 text-sm" data-id="element-197">
                  No estás dispuesto a ir al correo y enviar una carta
                  certificada (el sistema requiere esta acción mínima).
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-198">
                <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-199" />
                <p className="font-body text-slate-300 text-sm" data-id="element-200">
                  Buscas estafar al sistema. Esto se basa en exigir exactitud
                  legal, <strong className="text-white" data-id="element-201">no en mentir</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function HowItWorksSection() {
  return <section className="py-16 px-4" style={{
    background: '#0d1117'
  }} data-id="element-202">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-203">
        <div className="text-center mb-12" data-id="element-204">
          <span className="inline-block bg-slate-800 text-slate-400 text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full border border-slate-700 mb-4" data-id="element-205">
            ASÍ DE SIMPLE
          </span>
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase" data-id="element-206">
            3 Pasos y el Sistema{' '}
            <span className="text-red-500" data-id="element-207">Trabaja por Ti</span>
          </h2>
        </div>

        <div className="space-y-6" data-id="element-208">
          <div className="flex items-start gap-6" data-id="element-209">
            <div className="step-number w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-210">
              <span className="font-heading font-black text-white text-2xl" data-id="element-211">
                1
              </span>
            </div>
            <div className="pt-2" data-id="element-212">
              <p className="font-heading font-bold text-white text-xl uppercase mb-2" data-id="element-213">
                Descarga el Protocolo
              </p>
              <p className="font-body text-slate-400 text-base" data-id="element-214">
                Acceso instantáneo. Elige la plantilla que se ajusta a tu
                situación específica.
              </p>
            </div>
          </div>

          <div className="section-divider ml-7" data-id="element-215" />

          <div className="flex items-start gap-6" data-id="element-216">
            <div className="step-number w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-217">
              <span className="font-heading font-black text-white text-2xl" data-id="element-218">
                2
              </span>
            </div>
            <div className="pt-2" data-id="element-219">
              <p className="font-heading font-bold text-white text-xl uppercase mb-2" data-id="element-220">
                Envía la Carta Certificada
              </p>
              <p className="font-body text-slate-400 text-base" data-id="element-221">
                Te enseñamos exactamente cómo hacerlo. Es el único paso físico
                requerido.
              </p>
            </div>
          </div>

          <div className="section-divider ml-7" data-id="element-222" />

          <div className="flex items-start gap-6" data-id="element-223">
            <div className="step-number w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-224">
              <span className="font-heading font-black text-white text-2xl" data-id="element-225">
                3
              </span>
            </div>
            <div className="pt-2" data-id="element-226">
              <p className="font-heading font-bold text-white text-xl uppercase mb-2" data-id="element-227">
                Disfruta el Silencio
              </p>
              <p className="font-body text-slate-400 text-base" data-id="element-228">
                El sistema legal trabaja para borrar el rastro de papel de tu
                historial.{' '}
                <strong className="text-green-400" data-id="element-229">
                  Tú solo esperas los resultados.
                </strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ProofSection() {
  return <section className="py-16 px-4 circuit-pattern" style={{
    background: 'linear-gradient(135deg, #0a0e1a, #0f1520)'
  }} data-id="element-230">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-231">
        <div className="text-center mb-10" data-id="element-232">
          <span className="inline-block bg-green-900 bg-opacity-50 text-green-400 text-xs font-bold font-body tracking-widest uppercase px-4 py-2 rounded-full border border-green-800 mb-4" data-id="element-233">
            QUÉ CAMBIA EN 7 DÍAS
          </span>
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase" data-id="element-234">
            Tu Nueva Realidad{' '}
            <span className="text-green-400" data-id="element-235">en Una Semana</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4" data-id="element-236">
          <div className="bg-green-900 bg-opacity-15 border border-green-700 border-opacity-30 rounded-xl p-5 flex items-start gap-4" data-id="element-237">
            <PhoneOffIcon className="w-8 h-8 text-green-400 flex-shrink-0" data-id="element-238" />
            <div data-id="element-239">
              <p className="font-body font-bold text-white mb-1" data-id="element-240">
                El teléfono deja de sonar.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-241">
                Silencio real. No más "Número Desconocido".
              </p>
            </div>
          </div>

          <div className="bg-green-900 bg-opacity-15 border border-green-700 border-opacity-30 rounded-xl p-5 flex items-start gap-4" data-id="element-242">
            <FileTextIcon className="w-8 h-8 text-green-400 flex-shrink-0" data-id="element-243" />
            <div data-id="element-244">
              <p className="font-body font-bold text-white mb-1" data-id="element-245">
                Carta oficial de cierre.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-246">
                La agencia admite que "no pudo verificar la deuda".
              </p>
            </div>
          </div>

          <div className="bg-green-900 bg-opacity-15 border border-green-700 border-opacity-30 rounded-xl p-5 flex items-start gap-4" data-id="element-247">
            <TrendingUpIcon className="w-8 h-8 text-green-400 flex-shrink-0" data-id="element-248" />
            <div data-id="element-249">
              <p className="font-body font-bold text-white mb-1" data-id="element-250">
                Tu puntaje de crédito sube.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-251">
                Eliminaste la basura que no debería estar ahí.
              </p>
            </div>
          </div>

          <div className="bg-green-900 bg-opacity-15 border border-green-700 border-opacity-30 rounded-xl p-5 flex items-start gap-4" data-id="element-252">
            <ShieldCheckIcon className="w-8 h-8 text-green-400 flex-shrink-0" data-id="element-253" />
            <div data-id="element-254">
              <p className="font-body font-bold text-white mb-1" data-id="element-255">
                Recuperas el control.
              </p>
              <p className="font-body text-slate-400 text-sm" data-id="element-256">
                Por el precio de una cena rápida.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    q: '¿Esto es legal?',
    a: 'Es 100% legal bajo las leyes federales FDCPA (Fair Debt Collection Practices Act) y FCRA (Fair Credit Reporting Act) de protección al consumidor. Estas son leyes federales de los Estados Unidos diseñadas específicamente para protegerte.'
  }, {
    q: '¿Tengo que hablar con un juez?',
    a: 'No. Todo el proceso se hace a través de cartas escritas enviadas por correo certificado. No hay cortes, no hay abogados, no hay audiencias.'
  }, {
    q: '¿Funciona si la deuda es vieja?',
    a: '¡Especialmente si es vieja! Esas son las deudas donde más documentos se pierden en el proceso de venta entre agencias. Las deudas antiguas son las más vulnerables a este protocolo.'
  }, {
    q: '¿Recibo el acceso ahora mismo?',
    a: 'Sí, el acceso es instantáneo tras el pago. Recibirás un email con el enlace de descarga en menos de 2 minutos.'
  }, {
    q: '¿Qué pasa si la agencia SÍ tiene los documentos?',
    a: 'En ese caso, el protocolo te ayuda a verificar que la deuda sea exactamente correcta en monto, fecha y acreedor. Cualquier inexactitud — incluso un centavo — les obliga a corregir o eliminar el registro.'
  }];
  return <section className="py-16 px-4" style={{
    background: '#0a0e1a'
  }} data-id="element-257">
      <div className="max-w-3xl mx-auto fade-in" data-id="element-258">
        <div className="text-center mb-10" data-id="element-259">
          <h2 className="font-heading font-bold text-white text-3xl md:text-4xl uppercase" data-id="element-260">
            Preguntas <span className="text-yellow-400" data-id="element-261">Frecuentes</span>
          </h2>
        </div>

        <div className="space-y-3" data-id="element-262">
          {faqs.map((faq, i) => <div key={i} className="card-dark rounded-xl overflow-hidden" data-id="element-263">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white hover:bg-opacity-5 transition-colors duration-200" aria-expanded={openIndex === i} data-id="element-264">
                <span className="font-body font-semibold text-white text-base pr-4" data-id="element-265">
                  {faq.q}
                </span>
                {openIndex === i ? <ChevronUpIcon className="w-5 h-5 text-yellow-400 flex-shrink-0" data-id="element-266" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-267" />}
              </button>
              {openIndex === i && <div className="px-5 pb-5" data-id="element-268">
                  <p className="font-body text-slate-300 text-sm leading-relaxed" data-id="element-269">
                    {faq.a}
                  </p>
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}
function OfferSection() {
  return <section id="oferta" className="py-16 px-4" style={{
    background: '#0d1117'
  }} data-id="element-270">
      <div className="max-w-2xl mx-auto fade-in" data-id="element-271">
        {/* Comparison */}
        <div className="text-center mb-10" data-id="element-272">
          <p className="font-body text-slate-400 text-base md:text-lg mb-2" data-id="element-273">
            No pagues{' '}
            <span className="line-through text-red-400 font-bold" data-id="element-274">$2,000</span>{' '}
            a una "empresa de reparación de crédito" que hará lo mismo que tú
            puedes hacer en <strong className="text-white" data-id="element-275">20 minutos</strong>.
          </p>
        </div>

        {/* Offer box */}
        <div className="rounded-2xl overflow-hidden" style={{
        background: 'linear-gradient(135deg, #111827, #0d1117)',
        border: '2px solid rgba(220, 38, 38, 0.4)',
        boxShadow: '0 0 60px rgba(220, 38, 38, 0.15)'
      }} data-id="element-276">
          {/* Header */}
          <div className="bg-red-600 py-4 px-6 text-center" data-id="element-277">
            <p className="font-heading font-bold text-white text-lg uppercase tracking-wide" data-id="element-278">
              Obtén el Protocolo Fantasma Completo Hoy
            </p>
          </div>

          <div className="p-8 md:p-10" data-id="element-279">
            {/* Product image */}
            <div className="flex justify-center mb-8" data-id="element-280">
              <img src="https://cdn.magicpatterns.com/uploads/8Rmf5AWfXXSWWPS6Ab3Eya/heylloyd-image-1772487533727.png" alt="Protocolo Fantasma Sin Deudas" className="w-48 md:w-56" style={{
              filter: 'drop-shadow(0 10px 30px rgba(220, 38, 38, 0.3))'
            }} data-id="element-281" />
            </div>

            {/* What's included */}
            <div className="space-y-2 mb-8" data-id="element-282">
              <div className="flex items-center gap-3" data-id="element-283">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-284" />
                <span className="font-body text-slate-300 text-sm" data-id="element-285">
                  Guía Maestra PDF "Fantasma Sin Deudas"
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-286">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-287" />
                <span className="font-body text-slate-300 text-sm" data-id="element-288">
                  Plantilla #1: El Disparador de Silencio
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-289">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-290" />
                <span className="font-body text-slate-300 text-sm" data-id="element-291">
                  Plantilla #2: El Escudo de Validación
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-292">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-293" />
                <span className="font-body text-slate-300 text-sm" data-id="element-294">
                  Plantilla #3: El Borrador del Buró
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-295">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-296" />
                <span className="font-body text-slate-300 text-sm" data-id="element-297">
                  El Guión de Poder de 10 Segundos
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="text-center mb-8" data-id="element-298">
              <p className="font-body text-slate-400 text-sm mb-1" data-id="element-299">
                Un solo pago de:
              </p>
              <div className="flex items-center justify-center gap-4" data-id="element-300">
                <span className="font-heading font-bold text-slate-500 line-through text-3xl" data-id="element-301">
                  $27
                </span>
                <span className="font-heading font-black text-yellow-400 price-glow" style={{
                fontSize: '5rem',
                lineHeight: 1
              }} data-id="element-302">
                  $6.39
                </span>
              </div>
              <p className="font-body text-green-400 text-sm font-semibold mt-2" data-id="element-303">
                🔥 Descuento especial de lanzamiento — ahorra $20.61
              </p>
              <p className="font-body text-slate-400 text-xs mt-1" data-id="element-304">
                Precio especial — puede subir en cualquier momento
              </p>
            </div>

            {/* CTA Button */}
            <a href="https://nexocompra.com/cart/47758248804490:1" target="_blank" rel="noopener noreferrer" className="btn-cta block w-full bg-red-600 hover:bg-red-500 text-white font-heading font-bold text-xl md:text-2xl uppercase tracking-wide text-center py-5 px-8 rounded-xl transition-colors duration-200 mb-4" data-id="element-305">
              SÍ, QUIERO VOLVERME INVISIBLE
              <br data-id="element-306" />
              <span className="text-base font-body font-normal normal-case tracking-normal" data-id="element-307">
                Y RECUPERAR MI PAZ AHORA →
              </span>
            </a>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs font-body text-slate-500" data-id="element-308">
              <span className="flex items-center gap-1" data-id="element-309">
                <LockIcon className="w-3 h-3" data-id="element-310" />
                Pago 100% Seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-311">
                <ZapIcon className="w-3 h-3 text-yellow-500" data-id="element-312" />
                Acceso Instantáneo
              </span>
              <span className="flex items-center gap-1" data-id="element-313">
                <ShieldCheckIcon className="w-3 h-3 text-green-500" data-id="element-314" />
                Garantía de Satisfacción
              </span>
            </div>
          </div>
        </div>

        {/* Guarantee */}
        <div className="guarantee-badge rounded-xl p-6 mt-6 text-center" data-id="element-315">
          <ShieldCheckIcon className="w-10 h-10 text-yellow-400 mx-auto mb-3" data-id="element-316" />
          <p className="font-heading font-bold text-white text-xl uppercase mb-2" data-id="element-317">
            Garantía de Satisfacción Total
          </p>
          <p className="font-body text-slate-400 text-sm leading-relaxed" data-id="element-318">
            Si el protocolo no detiene las llamadas o no ves resultados en tu
            crédito, te devolvemos cada centavo. Sin preguntas. Sin drama.
          </p>
        </div>
      </div>
    </section>;
}
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const isPrivacy = type === 'privacy';
  const privacyContent = <>
      <h2 className="font-heading font-bold text-white text-2xl uppercase mb-6" data-id="element-319">
        Política de Privacidad
      </h2>
      <p className="font-body text-slate-400 text-xs mb-4" data-id="element-320">
        Última actualización: 2025
      </p>

      <div className="space-y-5 font-body text-slate-300 text-sm leading-relaxed" data-id="element-321">
        <div data-id="element-322">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-323">
            1. Información que Recopilamos
          </h3>
          <p data-id="element-324">
            Al realizar una compra, recopilamos únicamente la información
            necesaria para procesar tu pedido: nombre, dirección de correo
            electrónico e información de pago (procesada de forma segura por
            nuestro proveedor de pagos). No almacenamos datos de tarjetas de
            crédito en nuestros servidores.
          </p>
        </div>
        <div data-id="element-325">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-326">
            2. Uso de la Información
          </h3>
          <p data-id="element-327">
            Utilizamos tu información exclusivamente para: (a) entregar el
            producto digital adquirido, (b) enviarte confirmaciones de compra y
            actualizaciones del producto, y (c) cumplir con obligaciones legales
            aplicables.
          </p>
        </div>
        <div data-id="element-328">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-329">
            3. Compartir Información
          </h3>
          <p data-id="element-330">
            No vendemos, alquilamos ni compartimos tu información personal con
            terceros, excepto con proveedores de servicios de pago y entrega de
            correo electrónico necesarios para completar tu compra, quienes
            están obligados contractualmente a proteger tu información.
          </p>
        </div>
        <div data-id="element-331">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-332">
            4. Cookies
          </h3>
          <p data-id="element-333">
            Este sitio puede utilizar cookies básicas para mejorar la
            experiencia de navegación y analizar el tráfico del sitio de forma
            anónima. No utilizamos cookies para rastrear información personal
            identificable.
          </p>
        </div>
        <div data-id="element-334">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-335">
            5. Seguridad
          </h3>
          <p data-id="element-336">
            Implementamos medidas de seguridad estándar de la industria para
            proteger tu información. Todas las transacciones se procesan
            mediante conexiones SSL encriptadas.
          </p>
        </div>
        <div data-id="element-337">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-338">
            6. Tus Derechos
          </h3>
          <p data-id="element-339">
            Tienes derecho a solicitar acceso, corrección o eliminación de tu
            información personal en cualquier momento. Para ejercer estos
            derechos, puedes comunicarte con nosotros a través de los canales de
            soporte disponibles tras tu compra.
          </p>
        </div>
        <div data-id="element-340">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-341">
            7. Cambios a esta Política
          </h3>
          <p data-id="element-342">
            Nos reservamos el derecho de actualizar esta política en cualquier
            momento. Los cambios serán publicados en esta página con la fecha de
            actualización correspondiente.
          </p>
        </div>
      </div>
    </>;
  const termsContent = <>
      <h2 className="font-heading font-bold text-white text-2xl uppercase mb-6" data-id="element-343">
        Términos de Servicio
      </h2>
      <p className="font-body text-slate-400 text-xs mb-4" data-id="element-344">
        Última actualización: 2025
      </p>

      <div className="space-y-5 font-body text-slate-300 text-sm leading-relaxed" data-id="element-345">
        <div data-id="element-346">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-347">
            1. Aceptación de Términos
          </h3>
          <p data-id="element-348">
            Al acceder y utilizar este sitio web y adquirir nuestros productos,
            aceptas quedar vinculado por estos Términos de Servicio. Si no estás
            de acuerdo con alguno de estos términos, no debes utilizar este
            sitio.
          </p>
        </div>
        <div data-id="element-349">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-350">
            2. Descripción del Producto
          </h3>
          <p data-id="element-351">
            El "Protocolo Fantasma Sin Deudas" es un producto de información
            digital (guía en PDF y plantillas) diseñado con fines educativos e
            informativos. El contenido se basa en leyes federales de protección
            al consumidor de los Estados Unidos (FDCPA y FCRA) disponibles
            públicamente.
          </p>
        </div>
        <div data-id="element-352">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-353">
            3. Aviso Legal Importante
          </h3>
          <p data-id="element-354">
            Este producto es material educativo e informativo únicamente.{' '}
            <strong className="text-white" data-id="element-355">
              No constituye asesoramiento legal.
            </strong>{' '}
            Los resultados pueden variar según la situación individual de cada
            persona. Para situaciones legales complejas, se recomienda consultar
            con un abogado licenciado. No somos un bufete de abogados ni
            prestamos servicios legales.
          </p>
        </div>
        <div data-id="element-356">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-357">
            4. Política de Reembolso
          </h3>
          <p data-id="element-358">
            Ofrecemos una garantía de satisfacción. Si no estás satisfecho con
            el producto, puedes solicitar un reembolso completo dentro de los
            términos establecidos al momento de la compra, contactando a nuestro
            equipo de soporte. Los reembolsos se procesan dentro de 5-10 días
            hábiles.
          </p>
        </div>
        <div data-id="element-359">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-360">
            5. Propiedad Intelectual
          </h3>
          <p data-id="element-361">
            Todo el contenido de este producto, incluyendo textos, plantillas y
            materiales, está protegido por derechos de autor. La compra te
            otorga una licencia personal, no transferible, para uso individual.
            Está prohibida la reproducción, distribución o reventa del material
            sin autorización expresa por escrito.
          </p>
        </div>
        <div data-id="element-362">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-363">
            6. Limitación de Responsabilidad
          </h3>
          <p data-id="element-364">
            En ningún caso seremos responsables por daños indirectos,
            incidentales o consecuentes derivados del uso o incapacidad de uso
            de este producto. Nuestra responsabilidad máxima se limita al monto
            pagado por el producto.
          </p>
        </div>
        <div data-id="element-365">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-366">
            7. Ley Aplicable
          </h3>
          <p data-id="element-367">
            Estos términos se rigen por las leyes aplicables de los Estados
            Unidos. Cualquier disputa será resuelta mediante arbitraje
            vinculante de acuerdo con las reglas de la American Arbitration
            Association.
          </p>
        </div>
        <div data-id="element-368">
          <h3 className="font-heading font-bold text-white text-base uppercase mb-2" data-id="element-369">
            8. Modificaciones
          </h3>
          <p data-id="element-370">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. El uso continuado del sitio después de dichos cambios
            constituye tu aceptación de los nuevos términos.
          </p>
        </div>
      </div>
    </>;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{
    background: 'rgba(0,0,0,0.85)'
  }} onClick={onClose} data-id="element-371">
      <div className="relative w-full max-w-2xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col" style={{
      background: 'linear-gradient(135deg, #111827, #0d1117)',
      border: '1px solid rgba(148, 163, 184, 0.15)',
      boxShadow: '0 25px 80px rgba(0,0,0,0.6)'
    }} onClick={e => e.stopPropagation()} data-id="element-372">
        {/* Modal header bar */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)'
      }} data-id="element-373">
          <span className="font-body text-slate-500 text-xs uppercase tracking-widest" data-id="element-374">
            {isPrivacy ? 'Política de Privacidad' : 'Términos de Servicio'}
          </span>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200" aria-label="Cerrar" data-id="element-375">
            <XCircleIcon className="w-5 h-5" data-id="element-376" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-6 py-6" data-id="element-377">
          {isPrivacy ? privacyContent : termsContent}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex-shrink-0 text-center" style={{
        borderTop: '1px solid rgba(148, 163, 184, 0.1)'
      }} data-id="element-378">
          <button onClick={onClose} className="font-body text-slate-400 hover:text-white text-sm transition-colors duration-200 underline underline-offset-2" data-id="element-379">
            Cerrar
          </button>
        </div>
      </div>
    </div>;
}
function Footer() {
  const [modal, setModal] = useState<ModalType>(null);
  return <>
      <LegalModal type={modal} onClose={() => setModal(null)} data-id="element-380" />
      <footer className="py-8 px-4 border-t border-slate-800" style={{
      background: '#0a0e1a'
    }} data-id="element-381">
        <div className="max-w-3xl mx-auto text-center" data-id="element-382">
          <p className="font-body text-slate-600 text-xs leading-relaxed mb-4" data-id="element-383">
            Este producto es para fines educativos e informativos. No constituye
            asesoramiento legal. Las leyes FDCPA y FCRA son leyes federales de
            los Estados Unidos. Los resultados pueden variar según cada
            situación individual. Siempre consulta con un profesional legal para
            casos complejos.
          </p>

          {/* Legal links */}
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-384">
            <button onClick={() => setModal('privacy')} className="font-body text-slate-500 hover:text-slate-300 text-xs transition-colors duration-200 underline underline-offset-2" data-id="element-385">
              Política de Privacidad
            </button>
            <span className="text-slate-700" data-id="element-386">·</span>
            <button onClick={() => setModal('terms')} className="font-body text-slate-500 hover:text-slate-300 text-xs transition-colors duration-200 underline underline-offset-2" data-id="element-387">
              Términos de Servicio
            </button>
          </div>

          <p className="font-body text-slate-700 text-xs" data-id="element-388">
            © 2025 Protocolo Fantasma Sin Deudas · Todos los derechos
            reservados
          </p>
        </div>
      </footer>
    </>;
}
export function LandingPage() {
  useScrollAnimation();
  return <div className="min-h-screen w-full" style={{
    background: '#0a0e1a'
  }} data-id="element-389">
      <StickyTopBar data-id="element-390" />
      <div className="pt-10" data-id="element-391">
        <HeroSection data-id="element-392" />
        <OpeningHookSection data-id="element-393" />
        <ProblemSection data-id="element-394" />
        <WhyFailedSection data-id="element-395" />
        <MechanismSection data-id="element-396" />
        <WhatYouGetSection data-id="element-397" />
        <ForWhoSection data-id="element-398" />
        <HowItWorksSection data-id="element-399" />
        <ProofSection data-id="element-400" />
        <FAQSection data-id="element-401" />
        <OfferSection data-id="element-402" />
        <Footer data-id="element-403" />
      </div>
    </div>;
}